import React, { useState } from 'react';
import { FaEdit, FaTrash, FaEye, FaSync } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Line, Pie, Doughnut, Scatter } from 'react-chartjs-2';
import ScatterChart from './ScatterChart';
import DonutChart from './DonutChart';
import { formatDateTime } from '../../../utils/formatUtils';
import './ChartDisplay.css';

const truncateCategoryLabel = (text, maxLen = 20) => {
  const s = String(text ?? '');
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
};

const formatCompactAxisNumber = (n) => {
  if (!Number.isFinite(n)) return '';
  if (n === 0) return '0';
  const sign = n < 0 ? '-' : '';
  const v = Math.abs(n);
  if (v >= 1e9) return `${sign}${(v / 1e9).toFixed(1).replace(/\.0$/, '')}B`;
  if (v >= 1e6) return `${sign}${(v / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (v >= 1e4) return `${sign}${(v / 1e3).toFixed(1).replace(/\.0$/, '')}k`;
  if (v >= 1000) return `${sign}${Math.round(v).toLocaleString()}`;
  if (Number.isInteger(n)) return `${sign}${Math.round(v)}`;
  return `${sign}${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const parseChartDataValue = (value) => {
  if (value === null || value === undefined) return NaN;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'object' && value !== null && 'y' in value) {
    return parseChartDataValue(value.y);
  }
  if (typeof value === 'string') {
    const t = value.trim();
    if (t === '' || t === 'N/A') return NaN;
    const cleaned = t.replace(/[₹$€£,\s]/g, '').replace(/%$/, '');
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : NaN;
  }
  return NaN;
};

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const ChartDisplay = ({
  chart,
  onEdit,
  onDelete,
  onView,
  isPreview = false,
  selectedFields = [],
  summarizedFields = {},
  selectedGroups = [],
  summarizedData = {}, // Add summarizedData prop
  previewData = [], // Add previewData for record count calculation
  displayAuditInfo = false // Add displayAuditInfo prop
}) => {
  // Bump to re-run render from latest props (no synthetic/dummy data)
  const [, setRenderNonce] = useState(0);
  const handleRefresh = () => setRenderNonce((n) => n + 1);

  // Helper function to calculate record count for a specific group
  const calculateRecordCountForGroup = (groupValue, groupField) => {
    if (!previewData || previewData.length === 0 || !groupField) {
      return 0;
    }

    // Filter records that belong to this group
    const recordsInGroup = previewData.filter(row => {
      const tableName = groupField.object_name || 'main';
      const aliasedFieldName = `${tableName}_${groupField.field_name}`;
      const fieldData = row[aliasedFieldName] || row[groupField.field_name];

      let rowGroupValue = 'N/A';
      if (fieldData) {
        if (typeof fieldData === 'object' && fieldData !== null) {
          rowGroupValue = fieldData.displayValue || fieldData.value || 'N/A';
        } else {
          rowGroupValue = fieldData;
        }
      }
      return rowGroupValue === groupValue;
    });

    return recordsInGroup.length;
  };

  // Extract data from enhanced summarizedData structure (same logic as ReportChart)
  const extractDataFromSummarizedData = () => {
    if (!summarizedData || Object.keys(summarizedData).length === 0) {
      return null;
    }

    // Get the first group field and first summarized field from metadata
    const metadata = summarizedData._metadata;
    if (!metadata || !metadata.group_fields || !metadata.summarized_fields) {
      return null;
    }

    // Use chart's configured fields
    const selectedCategoryField = chart.xAxisField || Object.keys(metadata.group_fields)[0];
    const selectedValueFields = chart.yAxisFields && chart.yAxisFields.length > 0
      ? chart.yAxisFields
      : (chart.yAxisField ? [chart.yAxisField] : [Object.keys(metadata.summarized_fields)[0]].filter(Boolean));

    if (!selectedCategoryField || !selectedValueFields || selectedValueFields.length === 0) {
      console.log('ChartDisplay: Missing required fields:', { selectedCategoryField, selectedValueFields });
      return null;
    }

    console.log('ChartDisplay: Using chart fields:', { selectedCategoryField, selectedValueFields });

    const groupData = summarizedData[selectedCategoryField];
    if (!groupData) {
      console.log('ChartDisplay: No group data found for field:', selectedCategoryField);
      return null;
    }

    const labels = [];
    const datasets = [];

    // Get all unique group values (categories)
    const allGroupValues = new Set();
    Object.keys(groupData).forEach(groupValue => {
      allGroupValues.add(groupValue);
    });

    labels.push(...Array.from(allGroupValues));

    // Create datasets for each selected value field
    selectedValueFields.forEach((valueFieldName, index) => {
      let fieldInfo;
      let isVirtualRecordCount = false;

      if (valueFieldName === 'record_count') {
        // Handle virtual record count field
        fieldInfo = {
          field_label: 'Record Count',
          field_name: 'record_count',
          aggregation: 'count',
          data_type: 'number',
          is_virtual: true
        };
        isVirtualRecordCount = true;
      } else {
        fieldInfo = metadata.summarized_fields[valueFieldName];
        if (!fieldInfo) return;
      }

      // Get color from fieldColors or use default
      const fieldColor = chart.fieldColors && chart.fieldColors[valueFieldName]
        ? chart.fieldColors[valueFieldName]
        : ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#4BC0C0', '#9966FF'][index % 8];

      const dataset = {
        label: `${fieldInfo.field_label || valueFieldName} (${fieldInfo.aggregation?.toUpperCase() || 'SUM'})`,
        data: [],
        backgroundColor: fieldColor,
        borderColor: chart.chartType === 'line' || chart.chartType === 'area' ? fieldColor : undefined,
        borderWidth: chart.chartType === 'line' || chart.chartType === 'area' ? 2 : 1,
        fill: chart.chartType === 'area'
      };

      // Extract values for this field across all categories
      labels.forEach(category => {
        let parsedValue = 0;

        if (isVirtualRecordCount) {
          // For record count, first try to use pre-calculated value from summarizedData
          if (summarizedData && summarizedData.record_count && summarizedData.record_count[category] !== undefined) {
            parsedValue = summarizedData.record_count[category];
            console.log('ChartDisplay: Using pre-calculated record_count for', category, ':', parsedValue);
          } else {
            // Fallback to calculating the actual number of records in each group
            const groupField = metadata.group_fields[selectedCategoryField];
            parsedValue = calculateRecordCountForGroup(category, groupField);
            console.log('ChartDisplay: Calculated record_count for', category, ':', parsedValue);
          }
        } else {
          // Handle regular summarized fields
          const groupSummary = groupData[category];
          if (groupSummary && groupSummary[valueFieldName]) {
            const rawValue = groupSummary[valueFieldName].value;
            console.log('ChartDisplay: Raw value for', valueFieldName, 'in', category, ':', rawValue);

            if (typeof rawValue === 'number') {
              parsedValue = rawValue;
            } else if (typeof rawValue === 'string') {
              if (rawValue === 'N/A' || rawValue.trim() === '') {
                parsedValue = 0;
              } else {
                let cleanValue = rawValue.replace(/[₹$,\s]/g, '').trim();
                if (cleanValue.endsWith('%')) {
                  cleanValue = cleanValue.replace('%', '');
                }
                parsedValue = parseFloat(cleanValue) || 0;
              }
            }
          }
        }

        dataset.data.push(parsedValue);
      });

      datasets.push(dataset);
      console.log('ChartDisplay: Created dataset for field', valueFieldName, ':', dataset);
    });

    console.log('ChartDisplay: Final labels:', labels);
    console.log('ChartDisplay: Final datasets:', datasets);

    // Get field details from metadata
    const groupFieldInfo = metadata.group_fields[selectedCategoryField];

    return {
      labels,
      datasets,
      groupFieldInfo,
      selectedValueFields,
      xAxisLabel: chart.xAxisLabel || groupFieldInfo?.field_label || 'Categories',
      yAxisLabel: chart.yAxisLabel || 'Values'
    };
  };

  // Create Y-axis label with aggregation info
  const createYAxisLabel = (fieldName) => {
    if (!fieldName) return 'Value';

    const field = selectedFields.find(f => f.field_name === fieldName);
    if (!field) return fieldName;

    const baseLabel = field.field_label || field.field_name;
    const isSummarized = summarizedFields[field.id];

    if (isSummarized && summarizedFields[field.id].aggregation) {
      const aggregation = summarizedFields[field.id].aggregation;
      return `${baseLabel} (${aggregation})`;
    }

    return baseLabel;
  };

  const renderChart = () => {
    try {
      // Use preview data if available, otherwise generate dynamic data
      let chartData;

      if (chart.previewData) {
        console.log('ChartDisplay: Using previewData:', chart.previewData);

        // Check if we have the new datasets structure (multiple values)
        if (chart.previewData.datasets && Array.isArray(chart.previewData.datasets)) {
          console.log('ChartDisplay: Using multiple datasets structure');
          chartData = {
            labels: Array.isArray(chart.previewData.labels) ? chart.previewData.labels : [],
            datasets: chart.previewData.datasets.map((dataset, index) => ({
              ...dataset,
              // Ensure proper styling for different chart types
              backgroundColor: chart.chartType === 'donut' || chart.chartType === 'area'
                ? ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#4BC0C0', '#9966FF']
                : dataset.backgroundColor,
              borderColor: chart.chartType === 'line' || chart.chartType === 'area'
                ? dataset.borderColor
                : undefined,
              borderWidth: chart.chartType === 'line' || chart.chartType === 'area' ? 2 : (dataset.borderWidth || 1),
              fill: chart.chartType === 'area'
            }))
          };
        } else {
          console.log('ChartDisplay: Using single dataset structure (backward compatibility)');
          // Convert old previewData structure to Chart.js format (backward compatibility)
          chartData = {
            labels: Array.isArray(chart.previewData.labels) ? chart.previewData.labels : [],
            datasets: [{
              label: chart.previewData.yAxisTitle || 'Values',
              data: Array.isArray(chart.previewData.data) ? chart.previewData.data : [],
              backgroundColor: chart.chartType === 'pie' || chart.chartType === 'donut' || chart.chartType === 'area'
                ? ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#4BC0C0', '#9966FF']
                : '#36A2EB',
              borderColor: chart.chartType === 'line' || chart.chartType === 'area'
                ? '#36A2EB'
                : undefined,
              borderWidth: chart.chartType === 'line' || chart.chartType === 'area' ? 2 : undefined,
              fill: chart.chartType === 'area'
            }]
          };
        }
      } else {
        console.log('ChartDisplay: No previewData, trying to extract from summarizedData');
        // Try to extract data from summarizedData first
        const extractedData = extractDataFromSummarizedData();
        if (extractedData) {
          console.log('ChartDisplay: Using extracted data from summarizedData:', extractedData);
          chartData = {
            labels: extractedData.labels,
            datasets: extractedData.datasets
          };
        } else {
          console.log('ChartDisplay: No summarized data for chart; using empty series (no placeholder dummy data)');
          chartData = { labels: [], datasets: [] };
        }
      }

      console.log('ChartDisplay: Final chartData:', chartData);

      if (!chartData || !Array.isArray(chartData.datasets)) {
        console.error('ChartDisplay: Invalid chartData structure:', chartData);
        chartData = { labels: [], datasets: [] };
      }

      const isDonutOrScatter = chart.chartType === 'donut' || chart.chartType === 'scatter';
      const isTable = chart.chartType === 'table';
      const hasSeries =
        Array.isArray(chartData.labels) &&
        chartData.labels.length > 0 &&
        Array.isArray(chartData.datasets) &&
        chartData.datasets.length > 0 &&
        chartData.datasets.some(
          (ds) => Array.isArray(ds.data) && ds.data.length > 0
        );
      if (!isDonutOrScatter && !isTable && !hasSeries) {
        return (
          <div className="chart-container">
            {chart.title ? <div className="chart-title">{chart.title}</div> : null}
            <div className="chart-placeholder" style={{ textAlign: 'center', color: '#6c757d', padding: '1rem' }}>
              No data available
            </div>
          </div>
        );
      }

      const getMaxValueFromDatasets = (datasets) => {
        let max = 0;
        datasets.forEach(dataset => {
          (dataset.data || []).forEach(value => {
            const numericValue = parseChartDataValue(value);
            if (Number.isFinite(numericValue)) {
              max = Math.max(max, numericValue);
            }
          });
        });
        return max;
      };

      const getPaddedMax = (maxValue) => {
        if (!Number.isFinite(maxValue) || maxValue <= 0) {
          return undefined;
        }
        if (maxValue <= 5) {
          return maxValue + 1;
        }
        if (maxValue <= 10) {
          return maxValue + 2;
        }
        if (maxValue <= 100) {
          return Math.ceil(maxValue * 1.1);
        }
        return Math.ceil(maxValue * 1.05);
      };

      const paddedMaxValue = getPaddedMax(getMaxValueFromDatasets(chartData.datasets));
      const labelCount = Array.isArray(chartData.labels) ? chartData.labels.length : 0;
      const categoryTickMaxChars = labelCount > 14 ? 14 : labelCount > 8 ? 18 : 28;
      const rotateCategories = labelCount > 6;
      const isArcChart = chart.chartType === 'pie' || chart.chartType === 'donut';

      const options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: isArcChart
            ? { top: 8, right: 12, bottom: 8, left: 12 }
            : {
                top: 4,
                right: 6,
                bottom: rotateCategories ? 10 : 4,
                left: 4
              }
        },
        datasets: {
          bar: {
            barPercentage: labelCount > 18 ? 0.55 : labelCount > 10 ? 0.65 : 0.78,
            categoryPercentage: labelCount > 18 ? 0.65 : 0.78
          }
        },
        elements: {
          bar: {
            borderRadius: 6,
            borderSkipped: false
          }
        },
        plugins: {
          legend: {
            position: chart.legendPosition || 'top',
            display: chart.chartType !== 'pie' && chart.chartType !== 'donut'
          },
          title: {
            display: false // We'll show title separately
          },
          tooltip: {
            enabled: chart.showTooltips !== undefined ? chart.showTooltips : true,
            callbacks: {
              title: (items) => {
                if (!items?.length) return '';
                const idx = items[0].dataIndex;
                const raw =
                  chartData.labels && chartData.labels[idx] !== undefined
                    ? chartData.labels[idx]
                    : items[0].label;
                return raw != null ? String(raw) : '';
              },
              label: function (context) {
                const raw =
                  context.parsed?.y != null
                    ? context.parsed.y
                    : context.parsed?.x;
                const value = typeof raw === 'number' ? raw : parseChartDataValue(raw);
                const formatted = Number.isFinite(value) ? value.toLocaleString() : String(raw);
                return `${context.dataset.label}: ${formatted}`;
              }
            }
          },
          datalabels: {
            display: function (context) {
              // For pie/donut charts, only show labels for significant slices
              if (chart.chartType === 'pie' || chart.chartType === 'donut') {
                const data = context.dataset.data;
                const dataIndex = context.dataIndex;
                const value = data[dataIndex];
                const total = data.reduce((a, b) => a + b, 0);
                const percentage = (value / total) * 100;

                // Only show if > 5% and showLabels is enabled
                return (chart.showLabels !== undefined ? chart.showLabels : false) && percentage > 5;
              }

              return chart.showLabels !== undefined ? chart.showLabels : false;
            },
            anchor: function (context) {
              if (isArcChart) {
                return 'center';
              }
              return 'center';
            },
            align: function (context) {
              if (isArcChart) {
                return 'center';
              }

              // Get the chart instance and data
              const chartInstance = context.chart;
              const data = context.dataset.data;
              const dataIndex = context.dataIndex;
              const value = data[dataIndex];

              // Get the y-axis scale to determine position
              const yScale = chartInstance.scales.y;
              if (!yScale) return 'top';

              const yPosition = yScale.getPixelForValue(value);
              const chartHeight = yScale.height;
              const topThreshold = chartHeight * 0.2; // 20% from top

              // If the data point is in the top 20% of the chart, position label below
              if (yPosition < topThreshold) {
                return 'bottom';
              }
              // Otherwise, position label above
              return 'top';
            },
            color: chart.labelColor || '#000000',
            font: {
              weight: '600',
              size: 10
            },
            formatter: function (value, context) {
              if (chart.chartType === 'pie' || chart.chartType === 'donut') {
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return `${percentage}%`;
              }
              const n = parseChartDataValue(value);
              return Number.isFinite(n) ? n.toLocaleString() : String(value ?? '');
            },
            offset: isArcChart ? 0 : 8,
            padding: 2,
            clamp: true,
            clip: true
          }
        },
        scales: chart.chartType !== 'pie' && chart.chartType !== 'donut' && chart.chartType !== 'table' ? {
          x: {
            title: {
              display: true,
              text: chart.xAxisLabel || chart.previewData?.xAxisTitle || 'Categories',
              font: {
                size: 12,
                weight: 'bold'
              }
            },
            ticks: {
              autoSkip: true,
              autoSkipPadding: 12,
              maxRotation: rotateCategories ? 55 : 0,
              minRotation: rotateCategories ? 40 : 0,
              maxTicksLimit: Math.min(16, Math.max(6, labelCount)),
              font: { size: labelCount > 12 ? 10 : 11 },
              callback: function (value) {
                const label = this.getLabelForValue(value);
                return truncateCategoryLabel(label, categoryTickMaxChars);
              }
            },
            grid: {
              display: labelCount <= 24
            }
          },
          y: {
            beginAtZero: true,
            ...(paddedMaxValue !== undefined ? { max: paddedMaxValue } : { grace: '8%' }),
            title: {
              display: true,
              text: chart.yAxisLabel || chart.previewData?.yAxisTitle || 'Values',
              font: {
                size: 12,
                weight: 'bold'
              }
            },
            ticks: {
              maxTicksLimit: 6,
              precision: 0,
              callback: (value) => formatCompactAxisNumber(value)
            }
          }
        } : undefined
      };

      switch (chart.chartType) {
        case 'bar':
          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="chart-render">
                <Bar data={chartData} options={options} />
              </div>
            </div>
          );

        case 'horizontalBar':
          // Enhanced options for horizontal bar charts with consistent bar thickness
          const horizontalBarOptions = {
            ...options,
            indexAxis: 'y',
            elements: {
              bar: {
                borderWidth: 1,
                borderSkipped: false,
              }
            },
            scales: {
              x: {
                beginAtZero: true,
                ...(paddedMaxValue !== undefined ? { max: paddedMaxValue } : { grace: '8%' }),
                grid: {
                  display: true
                },
                ticks: {
                  maxTicksLimit: 6,
                  callback: (value) => formatCompactAxisNumber(value)
                },
                title: {
                  display: true,
                  text: chart.yAxisLabel || chart.previewData?.yAxisTitle || 'Values',
                  font: { size: 12, weight: 'bold' }
                }
              },
              y: {
                grid: {
                  display: false
                },
                title: {
                  display: true,
                  text: chart.xAxisLabel || chart.previewData?.xAxisTitle || 'Categories',
                  font: { size: 12, weight: 'bold' }
                },
                ticks: {
                  autoSkip: true,
                  maxTicksLimit: Math.min(18, Math.max(8, labelCount)),
                  font: { size: labelCount > 12 ? 10 : 11 },
                  callback: function (value) {
                    const label = this.getLabelForValue(value);
                    return truncateCategoryLabel(label, categoryTickMaxChars);
                  }
                }
              }
            },
            plugins: {
              ...options.plugins,
              legend: {
                ...options.plugins.legend,
                position: 'bottom'
              }
            }
          };

          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="chart-render">
                <Bar data={chartData} options={horizontalBarOptions} />
              </div>
            </div>
          );

        case 'line':
          // Enhanced options for line charts
          const lineOptions = {
            ...options,
            elements: {
              line: {
                tension: 0.4, // Smooth curves
                borderWidth: 3,
                fill: false
              },
              point: {
                radius: 4,
                hoverRadius: 6,
                borderWidth: 2
              }
            },
            interaction: {
              intersect: false,
              mode: 'index'
            },
            plugins: {
              ...options.plugins,
              tooltip: {
                ...options.plugins.tooltip,
                mode: 'index',
                intersect: false
              }
            }
          };

          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="chart-render">
                <Line data={chartData} options={lineOptions} />
              </div>
            </div>
          );

        case 'scatter':
          // Create a chartConfig object that matches what ScatterChart expects
          const chartConfig = {
            title: chart.title,
            scatterXAxis: chart.scatterXAxis,
            scatterYAxis: chart.scatterYAxis,
            scatterGroupField: chart.scatterGroupField,
            scatterXAxisLabel: chart.scatterXAxisLabel,
            scatterYAxisLabel: chart.scatterYAxisLabel,
            legendPosition: chart.legendPosition,
            showTooltips: chart.showTooltips,
            showLabels: chart.showLabels,
            labelColor: chart.labelColor
          };

          return (
            <ScatterChart
              chartConfig={chartConfig}
              chartTitle={chart.title}
              summarizedData={summarizedData}
              chartData={chartData}
            />
          );

        case 'pie':
          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="chart-render">
                <Pie data={chartData} options={options} />
              </div>
            </div>
          );

        case 'donut':
          // Create a chartConfig object that matches what DonutChart expects
          const donutChartConfig = {
            title: chart.title,
            chartType: 'donut',
            pieValueField: chart.pieValueField,
            pieSliceField: chart.pieSliceField,
            showValues: chart.showValues,
            showPercentages: chart.showPercentages,
            showTotal: chart.showTotal,
            legendColors: chart.legendColors,
            legendPosition: chart.legendPosition,
            valueFieldType: chart.valueFieldType,
            showTooltips: chart.showTooltips
          };

          return (
            <DonutChart
              chartConfig={donutChartConfig}
              chartTitle={chart.title}
              summarizedData={summarizedData}
              chartData={chartData}
              previewData={previewData}
              selectedGroups={selectedGroups}
            />
          );

        case 'area':
          // Create proper area chart data with fill configurations
          const areaChartData = {
            ...chartData,
            datasets: chartData.datasets.map(dataset => ({
              ...dataset,
              fill: true,
              tension: 0.4,
              borderWidth: 2,
              pointRadius: 3,
              pointHoverRadius: 5,
              backgroundColor: dataset.backgroundColor + '40', // Add transparency
              borderColor: dataset.borderColor
            }))
          };

          // Enhanced options for area charts
          const areaOptions = {
            ...options,
            elements: {
              line: {
                tension: 0.4, // Smooth curves
                borderWidth: 2
              },
              point: {
                radius: 3,
                hoverRadius: 5,
                borderWidth: 2
              }
            },
            interaction: {
              intersect: false,
              mode: 'index'
            },
            plugins: {
              ...options.plugins,
              tooltip: {
                ...options.plugins.tooltip,
                mode: 'index',
                intersect: false
              }
            },
            scales: {
              ...options.scales,
              x: {
                ...options.scales?.x,
                stacked: false // Allow overlapping areas
              },
              y: {
                ...options.scales?.y,
                stacked: false, // Allow overlapping areas
                beginAtZero: true
              }
            }
          };

          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="chart-render">
                <Line data={areaChartData} options={areaOptions} />
              </div>
            </div>
          );

        case 'table':
          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="table-render">
                {renderTable()}
              </div>
            </div>
          );

        default:
          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="chart-render">
                <Bar data={chartData} options={options} />
              </div>
            </div>
          );
      }
    } catch (error) {
      console.error('ChartDisplay: Error rendering chart:', error);
      return (
        <div className="chart-error">
          <div className="error-icon">⚠️</div>
          <p>Error rendering chart</p>
          <small>{error.message}</small>
        </div>
      );
    }
  };

  const renderTable = () => {
    let chartData;

    if (chart.previewData) {
      chartData = {
        labels: chart.previewData.labels || [],
        datasets: [{
          data: chart.previewData.data || []
        }]
      };
    } else {
      const extractedData = extractDataFromSummarizedData();
      if (extractedData) {
        chartData = {
          labels: extractedData.labels,
          datasets: extractedData.datasets
        };
      } else {
        chartData = { labels: [], datasets: [] };
      }
    }

    // Handle both single and multiple Y-axis fields
    const yAxisFields = chart.yAxisFields || (chart.yAxisField ? [chart.yAxisField] : []);

    const tableData = chartData.labels.map((label, index) => {
      const row = { [chart.xAxisField || 'Category']: label };

      // Add data for each Y-axis field
      yAxisFields.forEach((fieldName, fieldIndex) => {
        const dataset = chartData.datasets[fieldIndex];
        row[fieldName] = dataset?.data[index] || 0;
      });

      return row;
    });

    return (
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>{chart.xAxisField || 'Category'}</th>
              {yAxisFields.map((fieldName, index) => (
                <th key={index}>{createYAxisLabel(fieldName)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, index) => (
              <tr key={index}>
                <td>{row[chart.xAxisField || 'Category']}</td>
                {yAxisFields.map((fieldName, fieldIndex) => (
                  <td key={fieldIndex}>{row[fieldName]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className={`chart-display ${isPreview ? 'preview' : ''}`}>
      <div className="chart-content">
        {renderChart()}

        {chart.description && (
          <div className="chart-description">
            {chart.description}
          </div>
        )}

        {displayAuditInfo && (
          <div className="chart-metadata">
            <div className="chart-type-badge">
              {(() => {
                const chartTypeLabels = {
                  'bar': 'Column',
                  'horizontalBar': 'Bar',
                  'line': 'Line',
                  'scatter': 'Scatter',
                  'pie': 'Pie',
                  'area': 'Area',
                  'table': 'Table'
                };
                return `${chartTypeLabels[chart.chartType] || chart.chartType} Chart`;
              })()}
            </div>

            {/* Chart Audit Information */}
            {(chart.createddate || chart.createdby || chart.lastmodifieddate || chart.lastmodifiedby) && (
              <div className="chart-audit-info">
                {chart.createdby && (
                  <div className="audit-item">
                    <span className="audit-label">Created by:</span>
                    <span className="audit-value">{chart.createdby}</span>
                  </div>
                )}
                {chart.createddate && (
                  <div className="audit-item">
                    <span className="audit-label">Created:</span>
                    <span className="audit-value">{formatDateTime(chart.createddate)}</span>
                  </div>
                )}
                {chart.lastmodifiedby && (
                  <div className="audit-item">
                    <span className="audit-label">Modified by:</span>
                    <span className="audit-value">{chart.lastmodifiedby}</span>
                  </div>
                )}
                {chart.lastmodifieddate && (
                  <div className="audit-item">
                    <span className="audit-label">Modified:</span>
                    <span className="audit-value">{formatDateTime(chart.lastmodifieddate)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Show view button in preview mode, all buttons in edit mode */}
      {(!isPreview || onView) && (
        <div className="chart-actions">
          {!isPreview && (
            <>
              <button
                className="chart-action-btn refresh-btn"
                onClick={handleRefresh}
                title="Refresh Data"
              >
                <FaSync />
              </button>
              <button
                className="chart-action-btn edit-btn"
                onClick={() => onEdit && onEdit(chart)}
                title="Edit Chart"
              >
                <FaEdit />
              </button>
              <button
                className="chart-action-btn delete-btn"
                onClick={() => onDelete && onDelete(chart)}
                title="Delete Chart"
              >
                <FaTrash />
              </button>
            </>
          )}
          {onView && (
            <button
              className="chart-action-btn view-btn"
              onClick={() => onView(chart)}
              title="View Chart"
            >
              <FaEye />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ChartDisplay;
